import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-orange.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-amber.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/car-service-cobb-county-airport";
const PAGE_TITLE = "Car Service Cobb County Airport | RYY McCollum Field Chauffeur";
const PAGE_DESC =
  "Luxury car service at Cobb County International Airport (RYY / McCollum Field). Mercedes-Benz Sprinter and SUV chauffeur transportation at Atlantic Aviation Kennesaw FBO.";

const faqs = [
  {
    q: "What FBO do you service at Cobb County International Airport?",
    a: "We provide chauffeur service at the Atlantic Aviation FBO at Cobb County International Airport (RYY / McCollum Field) in Kennesaw, Georgia.",
  },
  {
    q: "How far is RYY from downtown Atlanta?",
    a: "Cobb County International Airport is approximately 35–45 minutes north of downtown Atlanta depending on traffic, and roughly 25 minutes from Buckhead.",
  },
  {
    q: "Do you offer transportation to North Georgia from RYY?",
    a: "Yes. RYY is a popular arrival point for clients heading to Lake Allatoona, the Blue Ridge Mountains, and other North Georgia destinations. We routinely provide long-distance Sprinter transfers from McCollum Field.",
  },
  {
    q: "Is RYY chauffeur service available 24/7?",
    a: "Yes. Our private aviation desk sends chauffeurs to McCollum Field around the clock, every day of the year.",
  },
  {
    q: "Where does the chauffeur meet us at McCollum Field?",
    a: "Atlantic Aviation's terminal is on the west side of the field off Cobb International Boulevard. Your chauffeur checks in at the front desk and stages in the FBO lot, then meets you in the lobby, or moves planeside when the line crew authorizes ramp access for your tail.",
  },
  {
    q: "Is RYY a better choice than PDK for a north-metro destination?",
    a: "Usually yes. If you are headed to Marietta, Kennesaw, Acworth, Cartersville, Lake Allatoona, or the North Georgia mountains, RYY saves you the I-285 crossing that a PDK arrival requires, often 30 minutes or more in afternoon traffic.",
  },
  {
    q: "Can you handle a group arriving at RYY on multiple aircraft?",
    a: "Yes. We stage vehicles against each tail number separately and hold the group in the FBO lounge until everyone is on the ground, then depart together.",
  },
  {
    q: "Do you provide as-directed hourly service from RYY?",
    a: "Yes. Hourly-as-directed is common for clients flying into McCollum Field for a day of site visits or meetings across Cobb and Bartow counties, with the chauffeur and vehicle staying with you between stops. Hourly service has a 4-hour minimum.",
  },
  {
    q: "What if our flight into RYY diverts or arrives early?",
    a: "We track the tail number rather than a scheduled time. An early arrival, a hold, or a diversion to PDK or FTY all reach your chauffeur automatically, and we reposition without you needing to call.",
  },
];

export const Route = createFileRoute("/car-service-cobb-county-airport")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "car service Cobb County Airport, RYY airport car service, McCollum Field chauffeur, Cobb County International Airport transportation, Atlantic Aviation Kennesaw car service, Kennesaw private jet car service",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Car Service Cobb County Airport",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Cobb County Airport Chauffeur Service",
            areaServed: [
              { type: "Airport", name: "Cobb County International Airport (RYY)" },
              { type: "City", name: "Kennesaw" },
              { type: "City", name: "Marietta" },
              { type: "City", name: "Atlanta" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="RYY • McCollum Field • Cobb County"
      h1="Car Service at Cobb County International Airport"
      intro="Landing at RYY? My chauffeurs meet private arrivals at McCollum Field with Suburban SUVs and Mercedes-Benz Sprinter Limousines for Atlanta's north side and beyond."
      heroImage={heroImage}
      heroAlt="Private jet on the runway at sunset at a North Georgia private airport (RYY)"
      midImage={midImage}
      midAlt="Luxury private jet cabin interior, executive arrival into Cobb County International (RYY)"
      sectionOneTitle="Your Ride From McCollum Field"
      sectionOneBody={[
        "Cobb County International Airport, also called McCollum Field, serves Kennesaw, Marietta, Acworth, Cartersville and Atlanta's north metro. My chauffeurs provide daily service at Atlantic Aviation.",
        "Choose a Suburban for a smaller party or a Sprinter for your group. We handle local transfers to Marietta and Buckhead, plus longer trips to Lake Allatoona, the Blue Ridge Mountains and Greenville.",
      ]}
      bulletsTitle="Why Choose Us at RYY"
      bulletsIntro="Your flight department and my chauffeurs working from the same plan."
      bullets={[
        "Atlantic Aviation FBO coverage",
        "Direct flight department coordination",
        "Live tail-number tracking",
        "Mercedes-Benz Sprinter Limousine fleet",
        "Suburban SUVs for principals, families and smaller parties, up to 6 or 7",
        "Long-distance North Georgia transfers",
      ]}
      coverageTitle="Common RYY Destinations"
      coverageIntro="Approximate transfer times from McCollum Field."
      coverage={[
        "Marietta, 15 min",
        "Kennesaw, 5 min",
        "Acworth, 15 min",
        "Buckhead, 35 min",
        "Midtown Atlanta, 40 min",
        "Downtown Atlanta, 45 min",
        "Lake Allatoona, 25 min",
        "Blue Ridge, 90 min",
        "ATL Airport, 50 min",
      ]}
      routeTable={{
        title: "RYY Drive Times and Route Detail",
        intro:
          "McCollum Field sits just off I-575 and Cobb Parkway in Kennesaw. These are the transfers we run most often out of RYY, with the roads your chauffeur actually uses.",
        rows: [
          {
            route: "RYY → Kennesaw / Town Center",
            distance: "~4 miles",
            time: "8–12 min",
            notes: "Cobb International Blvd to Chastain Rd. Shortest transfer on the field.",
          },
          {
            route: "RYY → Marietta Square",
            distance: "~9 miles",
            time: "15–22 min",
            notes: "Cobb Pkwy south. Add time on Saturday mornings around the Square.",
          },
          {
            route: "RYY → Acworth",
            distance: "~9 miles",
            time: "12–20 min",
            notes: "I-75 north one exit, then Cowan Rd toward the lake district.",
          },
          {
            route: "RYY → Truist Park / The Battery",
            distance: "~14 miles",
            time: "20–35 min",
            notes: "I-75 south to Windy Hill. Game-day traffic can double this, we stage early.",
          },
          {
            route: "RYY → Buckhead",
            distance: "~25 miles",
            time: "35–55 min",
            notes: "I-75 south to I-285 east, or Northside Dr when the interstate is heavy.",
          },
          {
            route: "RYY → Midtown Atlanta",
            distance: "~28 miles",
            time: "40–60 min",
            notes: "I-75 south to the Connector. Southbound is worst 7–9am.",
          },
          {
            route: "RYY → Downtown Atlanta",
            distance: "~30 miles",
            time: "45–65 min",
            notes: "Straight down I-75. Event nights need a 20-minute buffer.",
          },
          {
            route: "RYY → Lake Allatoona",
            distance: "~15 miles",
            time: "20–30 min",
            notes: "I-75 north to Glade Rd or Bells Ferry, depending on your marina.",
          },
          {
            route: "RYY → Blue Ridge / North Georgia",
            distance: "~80 miles",
            time: "90–110 min",
            notes: "I-575 north to GA-515. A frequent Sprinter run for cabin and winery trips.",
          },
          {
            route: "RYY → Hartsfield-Jackson (ATL)",
            distance: "~40 miles",
            time: "50–75 min",
            notes: "For private-to-commercial connections. We build in extra check-in time.",
          },
        ],
        footnote:
          "Drive times are typical door-to-door ranges including FBO staging. Your chauffeur monitors traffic live and reroutes as needed.",
      }}
      detailSections={[
        {
          title: "Arriving at the Atlantic Aviation FBO at RYY",
          body: [
            "McCollum Field runs a single full-service FBO, Atlantic Aviation, on the west side of the field off Cobb International Boulevard. That simplicity is part of why north-metro flight departments like it: one lobby, one line crew, no terminal confusion.",
          ],
          list: [
            "Chauffeur checks in at the Atlantic Aviation front desk before your aircraft is on final.",
            "Standard meet is in the FBO lobby; planeside loading when the line crew authorizes ramp access.",
            "Vehicle staged in the FBO lot, engine running and climate set, so there is no wait at the door.",
            "Late-night and pre-dawn arrivals covered, our team does not keep FBO hours.",
          ],
        },
        {
          title: "Why North-Metro Clients Choose RYY Over PDK",
          body: [
            "If your destination is north or west of the Perimeter, RYY almost always beats PDK on ground time. A PDK arrival headed to Marietta or Kennesaw means crossing I-285 in whatever traffic the hour brings, routinely 30 to 50 minutes of driving that an RYY arrival simply skips.",
            "McCollum Field is also the natural gateway for North Georgia. Lake Allatoona is under half an hour away, and the run up I-575 to Blue Ridge, Ellijay, and the mountain cabin corridor is a comfortable Sprinter ride rather than an endurance test in a sedan.",
            "The tradeoff runs the other way for downtown and southside destinations, where PDK or FTY will usually be quicker. If you are choosing between fields, tell us your destination and we will tell you honestly which one lands you closer.",
          ],
        },
        {
          title: "Which Vehicle Fits Your RYY Party",
          list: [
            "1–3 passengers with light bags, Suburban SUV for the fastest turn at the FBO door.",
            "4–8 passengers, executive travel, Sprinter Executive with facing captain's chairs and worktable.",
            "8–14 passengers, groups and family trips, Mercedes-Benz Sprinter Limousine with full luggage capacity.",
            "North Georgia long-haul, Sprinter configuration with cargo space retained for skis, clubs, and cabin gear.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "Our flagship private aviation chauffeur page.",
        },
        {
          to: "/fbo-transportation-atlanta",
          title: "FBO Transportation Atlanta",
          desc: "Coverage at every major Atlanta FBO.",
        },
        {
          to: "/atlanta-private-airport-transfer",
          title: "Atlanta Private Airport Transfer",
          desc: "Premium transfers from any private airport in metro Atlanta.",
        },
      ]}
      ctaTitle="Text Me Your RYY Plans"
      ctaBody="Send me your tail number, arrival time, destination, passengers and bags. I'll check availability personally and send your quote in writing."
    />
  );
}
